import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";
import { formatCurrency } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { TermsCalculatedValue, TermsUpdateKind } from "./types";

interface Props {
  values: {
    mrr: TermsCalculatedValue;
    cmrr: TermsCalculatedValue;
    annualContractValue: TermsCalculatedValue;
    totalContractValue: TermsCalculatedValue;
  };
  currency: string | null;
  updateKind: TermsUpdateKind;
  isRefreshing?: boolean;
  /** Last preview failed: the figures no longer describe the pending set. */
  isStale?: boolean;
}

interface RowProps {
  labelKey: string;
  fullLabelKey: string;
  value: TermsCalculatedValue;
  currency: string | null;
  showChange: boolean;
  emphasized?: boolean;
}

// formatCurrency displays cents, so a change is real at cent precision — rounding
// to whole units hid any difference under 50c while both columns showed it.
const isChanged = (value: TermsCalculatedValue): boolean =>
  value.change != null && Math.round(value.change * 100) !== 0;

const ValueRow: React.FC<RowProps> = ({
  labelKey,
  fullLabelKey,
  value,
  currency,
  showChange,
  emphasized
}) => {
  const { t } = useTranslation();
  const changed = isChanged(value);

  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-center gap-1">
        <Text variant="bodySecondary">{t(labelKey)}</Text>
        <Text variant="bodyTertiary">({t(fullLabelKey)})</Text>
      </div>
      <div className="flex items-center">
        <Text
          variant="bodyTertiary"
          className={cn("w-32 text-right", changed && "line-through")}
        >
          {formatCurrency(value.before, currency)}
        </Text>
        <Text
          variant={emphasized ? "lgSubtitle" : "body"}
          className={cn("w-32 text-right", emphasized && "font-semibold")}
        >
          {/* No `?? value.before`: a null New means the server could not compute
              the figure, and showing Current in its place reads as "this edit
              changes nothing". formatCurrency renders null as an em dash. */}
          {formatCurrency(value.after, currency)}
        </Text>
        {showChange ? (
          <Text
            variant="body"
            className={cn(
              "w-28 text-right",
              changed && (value.change! > 0 ? "text-positive" : "text-error")
            )}
          >
            {changed
              ? `${value.change! > 0 ? "+" : "-"}${formatCurrency(Math.abs(value.change!), currency)}`
              : ""}
          </Text>
        ) : null}
      </div>
    </div>
  );
};

// Both columns come from the preview endpoint, which runs the save's own computation.
const TermsCalculatedValuesFooter: React.FC<Props> = ({
  values,
  currency,
  updateKind,
  isRefreshing = false,
  isStale = false
}) => {
  const { t } = useTranslation();
  const showChange = [
    values.mrr,
    values.cmrr,
    values.annualContractValue,
    values.totalContractValue
  ].some(isChanged);

  return (
    <div
      className={cn(
        "flex flex-col w-full -mt-3 pt-6 border border-border-border1 rounded-b-xl px-5 pb-2.5",
        (isRefreshing || isStale) && "opacity-60"
      )}
      data-test="terms-calculated-values"
    >
      <div className="flex items-center justify-between mb-2">
        <Text variant="bodyTertiary">
          {t("contractTerms.calculatedValues")}
        </Text>
        <div className="flex items-center">
          <Text variant="bodyTertiary" className="w-32 text-right">
            {t("contracts.updateTerms.calculated.current")}
          </Text>
          <Text variant="bodyTertiary" className="w-32 text-right">
            {t(
              updateKind === TermsUpdateKind.CORRECTION
                ? "contracts.updateTerms.calculated.afterCorrection"
                : "contracts.updateTerms.calculated.new"
            )}
          </Text>
          {showChange ? (
            <Text variant="bodyTertiary" className="w-28 text-right">
              {t("contracts.updateTerms.calculated.change")}
            </Text>
          ) : null}
        </div>
      </div>
      <ValueRow
        labelKey="contractTerms.mrr"
        fullLabelKey="contractTerms.mrrFull"
        value={values.mrr}
        currency={currency}
        showChange={showChange}
      />
      <ValueRow
        labelKey="contractTerms.cmrr"
        fullLabelKey="contractTerms.cmrrFull"
        value={values.cmrr}
        currency={currency}
        showChange={showChange}
      />
      <ValueRow
        labelKey="contractTerms.acv"
        fullLabelKey="contractTerms.acvFull"
        value={values.annualContractValue}
        currency={currency}
        showChange={showChange}
      />
      <ValueRow
        labelKey="contractTerms.tcv"
        fullLabelKey="contractTerms.tcvFull"
        value={values.totalContractValue}
        currency={currency}
        showChange={showChange}
        emphasized
      />
    </div>
  );
};

export default TermsCalculatedValuesFooter;
